import React, { Component, ReactNode } from "react";
import ErrorPageTemplate from "./ErrorPageTemplate";
import { errorMonitor } from "../utils/errorMonitoring";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorId: "",
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Gera um ID único para o erro para referência no suporte
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Log seguro do erro
    try {
      console.error("ErrorBoundary caught error:", error);
    } catch (logError) {
      // Se até o log falhar, apenas ignore
    }

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Registrar o erro com o sistema de monitoramento de forma segura
    try {
      this.logError(error, errorInfo);
    } catch (logError) {
      // Se o log falhar, apenas continuar - não queremos que o ErrorBoundary falhe
      console.warn("Failed to log error to monitoring system:", logError);
    }
  }

  private logError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Usar o sistema de monitoramento centralizado
    errorMonitor.logComponentError("ErrorBoundary", error, errorInfo);

    // Log detalhado em desenvolvimento para facilitar debugging
    if (process.env.NODE_ENV === "development") {
      console.group("🚨 Error Boundary Caught an Error (DEVELOPMENT)");
      console.error("Error ID:", this.state.errorId);
      console.error("Error:", error);
      console.error("Component Stack:", errorInfo.componentStack);
      console.error("Error Stack:", error.stack);
      console.log(
        "💡 This error was caught by ErrorBoundary - check your React components!"
      );
      console.groupEnd();
    } else {
      // Em produção, log mais discreto
      console.error(
        `ErrorBoundary caught error [${this.state.errorId}]:`,
        error.message
      );
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorId: "",
    });
  };

  private handleReportProblem = () => {
    const { errorId, error } = this.state;
    const errorSummary = errorMonitor.getErrorSummary();

    const subject = `Erro na Aplicação - ${errorId}`;
    const body = `Olá,

Ocorreu um erro na aplicação Maputo Bike Style.

INFORMAÇÕES DO ERRO:
- ID do Erro: ${errorId}
- Mensagem: ${error?.message || "Erro desconhecido"}
- Página: ${window.location.href}
- Data/Hora: ${new Date().toLocaleString()}

RESUMO DA SESSÃO:
- Total de Erros: ${errorSummary.totalErrors}
- Erros Críticos: ${errorSummary.criticalErrors}
- Erros de API: ${errorSummary.apiErrors}
- Tempo de Sessão: ${Math.round(errorSummary.sessionDuration / 1000)}s
- User Agent: ${navigator.userAgent}

Por favor, investiguem este problema.

Obrigado.`;

    const mailtoLink = `mailto:tech@maputobikestyle.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  private handleViewErrorDetails = () => {
    const errorReport = errorMonitor.getErrorReport();

    // Criar uma nova janela com os detalhes do erro
    const errorWindow = window.open(
      "",
      "_blank",
      "width=800,height=600,scrollbars=yes"
    );
    if (errorWindow) {
      errorWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Erros - ${this.state.errorId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { background: #f8f9fa; padding: 20px; border-radius: 5px; margin-bottom: 20px; }
            .section { margin-bottom: 30px; }
            .error-item { background: #fff3cd; border: 1px solid #ffeaa7; padding: 15px; margin: 10px 0; border-radius: 5px; }
            .critical { background: #f8d7da; border-color: #f5c6cb; }
            .high { background: #fff3cd; border-color: #ffeaa7; }
            .medium { background: #cfe2ff; border-color: #b6d4fe; }
            .low { background: #f8f9fa; border-color: #dee2e6; }
            pre { background: #f8f9fa; padding: 10px; border-radius: 3px; overflow-x: auto; }
            .metric { display: inline-block; margin: 5px 10px; padding: 5px 10px; background: #e9ecef; border-radius: 3px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório de Erros - ${this.state.errorId}</h1>
            <p><strong>Sessão:</strong> ${errorReport.sessionId}</p>
            <p><strong>Data/Hora:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Página:</strong> ${window.location.href}</p>
          </div>
          
          <div class="section">
            <h2>Resumo da Sessão</h2>
            <div class="metric">Total de Erros: ${errorReport.errors.length}</div>
            <div class="metric">Duração: ${Math.round((Date.now() - errorReport.startTime) / 1000)}s</div>
            <div class="metric">Métricas de Performance: ${errorReport.performance.length}</div>
          </div>
          
          <div class="section">
            <h2>Erros Registrados</h2>
            ${errorReport.errors
              .map(
                (err) => `
              <div class="error-item ${err.severity}">
                <h4>${err.type} - ${err.severity}</h4>
                <p><strong>Mensagem:</strong> ${err.message}</p>
                <p><strong>Hora:</strong> ${new Date(err.timestamp).toLocaleString()}</p>
                ${err.stack ? `<pre>${err.stack}</pre>` : ""}
                ${err.metadata ? `<p><strong>Metadata:</strong> ${JSON.stringify(err.metadata, null, 2)}</p>` : ""}
              </div>
            `
              )
              .join("")}
          </div>
          
          <div class="section">
            <h2>Métricas de Performance</h2>
            ${errorReport.performance
              .slice(-10)
              .map(
                (metric) => `
              <div class="error-item low">
                <h4>${metric.name} (${metric.type})</h4>
                <p><strong>Duração:</strong> ${metric.duration.toFixed(2)}ms</p>
                <p><strong>Hora:</strong> ${new Date(metric.timestamp).toLocaleString()}</p>
                ${metric.metadata ? `<p><strong>Metadata:</strong> ${JSON.stringify(metric.metadata, null, 2)}</p>` : ""}
              </div>
            `
              )
              .join("")}
          </div>
          
          <script>
            // Permitir copiar o relatório
            document.addEventListener('keydown', function(e) {
              if (e.ctrlKey && e.key === 'a') {
                document.execCommand('selectAll');
              }
            });
          </script>
        </body>
        </html>
      `);
      errorWindow.document.close();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorPageTemplate
          errorCode="500"
          title="Oops! Algo correu mal"
          description="Ocorreu um erro inesperado na aplicação. A nossa equipa foi notificada automaticamente e está a trabalhar para resolver o problema."
          icon="ti-alert-triangle"
          iconColor="#ffc107"
          primaryAction={{
            label: "Tentar Novamente",
            onClick: this.handleRetry,
          }}
          secondaryAction={{
            label: "Voltar ao Início",
            href: "/",
          }}
          additionalInfo={
            <div className="mt-4">
              <div className="alert alert-light border">
                <div className="d-flex align-items-center mb-3">
                  <i className="ti ti-info-circle text-info me-2"></i>
                  <strong>Informações Técnicas</strong>
                </div>

                <div className="row text-start">
                  <div className="col-md-6">
                    <p className="mb-2 small">
                      <strong>ID do Erro:</strong>
                      <br />
                      <code className="text-break">{this.state.errorId}</code>
                    </p>
                    <p className="mb-2 small">
                      <strong>Mensagem:</strong>
                      <br />
                      <span className="text-muted">
                        {this.state.error?.message ||
                          "Erro interno da aplicação"}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-2 small">
                      <strong>Hora:</strong>
                      <br />
                      <span className="text-muted">
                        {new Date().toLocaleString()}
                      </span>
                    </p>
                    <p className="mb-2 small">
                      <strong>Página:</strong>
                      <br />
                      <span className="text-muted text-break">
                        {window.location.pathname}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <button
                    className="btn btn-sm btn-outline-info"
                    onClick={this.handleReportProblem}
                  >
                    <i className="ti ti-bug me-1"></i>
                    Reportar Problema
                  </button>

                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={this.handleViewErrorDetails}
                  >
                    <i className="ti ti-file-text me-1"></i>
                    Ver Detalhes
                  </button>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      errorMonitor.clearData();
                      window.location.reload();
                    }}
                  >
                    <i className="ti ti-refresh me-1"></i>
                    Reiniciar Aplicação
                  </button>
                </div>

                <div className="mt-3 pt-3 border-top">
                  <small className="text-muted">
                    <i className="ti ti-shield-check me-1"></i>
                    Este erro foi registrado automaticamente para análise.
                    Nenhuma informação pessoal é coletada.
                  </small>
                </div>
              </div>
            </div>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
